import { useEffect, useRef, useState } from 'react';
import { MaybePromise } from '@reduxjs/toolkit/dist/query/tsHelpers';

/**
 * Use this to inject a DOM element (or text string) from the host website
 * into the application.
 *
 * Usage:
 *
 * ```
 * const callback = useCallback(() => {
 *     return await callHostWebsiteHere(someId);
 * }, [someId]);
 *
 * const { destinationRef, plainText } = useExternalRender(callback);
 *
 * return (
 *     <div ref={destinationRef}>
 *         {plainText && <p>{plainText}</p>}
 *     </div>
 * );
 * ```
 */
export const useExternalRender = (
    callback: () => ExternalDomContent
) => {

    const destinationRef = useRef<HTMLDivElement>(null);
    const [renderedContent, setRenderedContent] = useState<ExpectedResponse>();
    const lastPromise = useRef<Promise<ExpectedResponse>>();

    useEffect(() => {

        setRenderedContent(undefined);

        const promise = Promise.resolve(callback());
        lastPromise.current = promise;
        promise.then(result => {
            if (promise === lastPromise.current) {
                setRenderedContent(result);
            }
        });

        return () => {
            lastPromise.current = undefined;
        };

    }, [callback]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {

        if (destinationRef.current
            && renderedContent
            && typeof renderedContent !== 'string') {

            const container = destinationRef.current;
            container.appendChild(renderedContent);

            return () => {
                container.removeChild(renderedContent);
            };
        }

    }, [renderedContent, destinationRef]);

    return {

        destinationRef,

        plainText: typeof renderedContent === 'string'
            ? renderedContent
            : undefined
    };
};

type ExpectedResponse = HTMLElement | string | undefined;
export type ExternalDomContent = MaybePromise<ExpectedResponse>;